import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import { Box, Button, Grid, Typography } from '@mui/material';
import ContactBox from 'components/lab-order/contact-box/contact-box.component';
import OrderFiles from 'components/order-flow/shared/order-files/order-files.component';
import { File as DenthubFile } from 'models/file';
import WorkFlowStep from 'components/shared/work-flow-step/work-flow-step.component';
import { OrderStep } from 'models/workflow-step';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import DeliveryDates from './delivery-dates/delivery-dates.component';
import { OrderInputType } from 'models/order-input/shared';
import SampleSection from './sample-section/sample-section.component';
import { LabClient } from 'models/client-directory';
import { useLazyQuery } from '@apollo/client';
import { getLabClientByIdTemplate } from 'apollo/templates/client-directory/query.graphql';
import { getOverriddenContact } from 'utils/get-overridden-contact';
import { Organization } from 'models/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useParams } from 'react-router-dom';
import { isValidDeliveryDate } from 'components/order-flow/shared/order-input-helper';

type DeliveryStepProps = {
    onBackClick: () => void;
    onNextClick: () => void;
    hideDeliveryDates?: boolean;
    hideWorkflow?: boolean;
};
const DeliveryStep: FC<DeliveryStepProps> = ({ onBackClick, onNextClick, hideDeliveryDates, hideWorkflow }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const params = useParams<{ id: string }>();
    const { order, setOrder } = useContext(LabOrderContext);
    const [clientSetting, setClientSetting] = useState<LabClient>({} as LabClient);
    const { showBoundary } = useDenthubErrorBoundary();

    const currentOrganization = currentOrgVar();
    const isLabWorkflowEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.LAB_WORKFLOW,
    );
    const isSampleMethodEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.SAMPLE_METHOD,
    );

    // Only a new delivery clinic needs its client setting. Keyed on all of deliveryDetails, every
    // date or time edit refetched it, and the new setting reset the dates it came from (DD-3648).
    useEffect(() => {
        if (order.deliveryDetails?.destination?._id) {
            getLabClientById({
                variables: {
                    customerId: order.deliveryDetails.destination._id,
                },
            });
        }
    }, [order.deliveryDetails?.destination?._id]);

    const [getLabClientById] = useLazyQuery(getLabClientByIdTemplate, {
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ labClientById: clientSetting }: { labClientById: LabClient }) => {
            setClientSetting(clientSetting);
            // If lab changed the contact information of simple type whitelabel clinic
            const overriddenContact = getOverriddenContact(clientSetting, order.customer as Organization);
            if (overriddenContact) {
                setOrder((prev) => ({
                    ...prev,
                    customer: {
                        ...prev.customer,
                        contact: overriddenContact,
                    },
                }));
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFilesChange = (files: DenthubFile[]) => {
        setOrder((prev) => ({ ...prev, files }));
    };

    const handleOrderStepChange = (step: OrderStep) => {
        setOrder((prev) => ({
            ...prev,
            workflowStep: step,
        }));
    };

    const showWorkflow = () => {
        return order.inputType === OrderInputType.NEW && isLabWorkflowEnabled && !hideWorkflow;
    };

    return (
        <Fragment>
            <Grid container spacing={2} alignItems="stretch" mb={4}>
                <Grid item sm={6}>
                    <ContactBox
                        organization={order.deliveryDetails.pickUpLocation!}
                        title={translate('orders:PICKUP_LOCATION')}
                    />
                </Grid>
                <Grid item sm={6}>
                    <ContactBox
                        organization={order.deliveryDetails.destination!}
                        title={translate('orders:DELIVERY_DESTINATION')}
                    />
                </Grid>

                {showWorkflow() ? (
                    <Fragment>
                        <Grid item sm={6}>
                            {isSampleMethodEnabled && <SampleSection />}
                            {!hideDeliveryDates && <DeliveryDates clientSetting={clientSetting} />}
                        </Grid>
                        <Grid item sm={6}>
                            <WorkFlowStep sample={order.sample} handleOrderStep={handleOrderStepChange} />
                        </Grid>
                    </Fragment>
                ) : (
                    <Fragment>
                        {isSampleMethodEnabled && (
                            <Grid item sm={hideWorkflow ? 12 : 6}>
                                <SampleSection />
                            </Grid>
                        )}
                        {!hideDeliveryDates && (
                            <Grid item sm={6}>
                                <DeliveryDates clientSetting={clientSetting} />
                            </Grid>
                        )}
                    </Fragment>
                )}
            </Grid>

            <Box>
                <Box mb={1}>
                    <Typography variant="h3">{translate('common:FILES')}</Typography>
                </Box>
                <OrderFiles order={order} onFilesChanged={handleFilesChange} />
            </Box>

            <Box display="flex" mt={2}>
                <Box flexGrow={1} textAlign="left">
                    <Button type="button" onClick={onBackClick} data-test="delivery-step.back">
                        {translate('common:BACK')}
                    </Button>
                </Box>
                <Box>
                    <Button
                        disabled={
                            (!hideDeliveryDates && !isValidDeliveryDate(order.deliveryDetails.date)) ||
                            (isSampleMethodEnabled && !order.sample.type) ||
                            (!hideWorkflow &&
                                !params.id &&
                                isLabWorkflowEnabled &&
                                currentOrganization.workFlow &&
                                !order.workflowStep?.name)
                        }
                        type="button"
                        onClick={onNextClick}
                        variant="contained"
                        data-test="delivery-step.next"
                    >
                        {translate('common:NEXT_STEP')}
                    </Button>
                </Box>
            </Box>
        </Fragment>
    );
};

export default DeliveryStep;
